
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/use-toast';

export interface EspaceStockage {
  id: string;
  user_id: string;
  bateau_id: string | null;
  nom: string;
  type: string;
  description?: string;
  capacite?: number;
  created_at: string;
  updated_at: string;
}

export const useEspacesStockage = () => {
  const [espaces, setEspaces] = useState<EspaceStockage[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchEspaces = async (bateauId?: string) => {
    if (!user) return;

    try {
      let query = supabase
        .from('espaces_stockage')
        .select('*')
        .eq('user_id', user.id);

      if (bateauId) {
        query = query.eq('bateau_id', bateauId);
      }

      const { data, error } = await query.order('nom', { ascending: true });

      if (error) {
        console.error('Erreur lors du chargement des espaces:', error);
      } else {
        setEspaces(data || []);
      }
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const ajouterEspace = async (espaceData: Omit<EspaceStockage, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const { data, error } = await supabase
        .from('espaces_stockage')
        .insert([{ ...espaceData, user_id: user.id }])
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de l\'ajout de l\'espace:', error);
        toast({
          title: "Erreur",
          description: "Impossible d'ajouter l'espace de stockage.",
          variant: "destructive",
        });
        return { error };
      } else {
        toast({
          title: "Succès",
          description: "Espace de stockage ajouté avec succès !",
        });
        await fetchEspaces(espaceData.bateau_id || undefined);
        return { data };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const modifierEspace = async (id: string, espaceData: Partial<EspaceStockage>) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const { data, error } = await supabase
        .from('espaces_stockage')
        .update(espaceData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la modification:', error);
        toast({
          title: "Erreur",
          description: "Impossible de modifier l'espace de stockage.",
          variant: "destructive",
        });
        return { error };
      } else {
        toast({
          title: "Succès",
          description: "Espace de stockage modifié avec succès !",
        });
        await fetchEspaces();
        return { data };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const supprimerEspace = async (id: string) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const { error } = await supabase
        .from('espaces_stockage')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Erreur lors de la suppression:', error);
        toast({
          title: "Erreur",
          description: "Impossible de supprimer l'espace de stockage.",
          variant: "destructive",
        });
        return { error };
      } else {
        toast({
          title: "Succès",
          description: "Espace de stockage supprimé avec succès !",
        });
        await fetchEspaces();
        return { success: true };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  useEffect(() => {
    fetchEspaces();
  }, [user]);

  return {
    espaces,
    loading,
    ajouterEspace,
    modifierEspace,
    supprimerEspace,
    refetchEspaces: fetchEspaces
  };
};
